import { useState, useEffect } from 'react';
import { Usd, PriceCaption } from '@/utils/CurrencyContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/utils/api';
import { toast } from 'sonner';
import { ArrowLeft, CreditCard, CheckCircle, AlertCircle, Building2, DollarSign, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';

const TeacherPaymentSettings = () => {
  const [bankDetails, setBankDetails] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    bank_name: '',
    account_holder_name: '',
    account_number: '',
    routing_number: '',
    swift_code: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [bankRes, earningsRes] = await Promise.all([
        api.get('/teacher/bank-details'),
        api.get('/teacher/earnings')
      ]);
      
      setBankDetails(bankRes.data);
      setEarnings(earningsRes.data);
      
      if (bankRes.data.has_bank_details) {
        setFormData({
          bank_name: bankRes.data.bank_name || '',
          account_holder_name: bankRes.data.account_holder_name || '',
          account_number: '', // Don't pre-fill for security
          routing_number: bankRes.data.routing_number || '',
          swift_code: bankRes.data.swift_code || ''
        });
      }
    } catch (error) {
      toast.error('Failed to load payment settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.bank_name || !formData.account_holder_name || !formData.account_number) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setIsSaving(true);
    try {
      await api.post('/teacher/bank-details', formData);
      toast.success('Bank details saved successfully!');
      fetchData(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save bank details');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAF9F6' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: '#163329' }}></div>
          <p style={{ color: '#5C635A' }}>Loading payment settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-6" style={{ backgroundColor: '#FAF9F6' }} data-testid="payment-settings-page">
      <div className="max-w-4xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/teacher/dashboard')}
          className="mb-4"
          data-testid="back-to-dashboard-button"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Dashboard
        </Button>

        <div className="mb-8">
          <h1
            className="text-4xl font-bold tracking-tight mb-2"
            style={{ fontFamily: 'Spectral, serif', color: '#163329' }}
          >
            Payment Status
          </h1>
          <p style={{ color: '#5C635A' }}>
            Enter your bank details to receive payments for your courses
          </p>
        </div>

        {/* Earnings Summary */}
        {earnings && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6"
          >
            <Card style={{ borderColor: '#E3E1DB' }}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <DollarSign className="h-8 w-8" style={{ color: '#163329' }} />
                  <div>
                    <p className="text-sm" style={{ color: '#5C635A' }}>Total Earnings</p>
                    <p className="text-2xl font-bold" style={{ color: '#163329' }}><Usd value={earnings.total_earnings} /></p>
                    <PriceCaption />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card style={{ borderColor: '#E3E1DB' }}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-8 w-8" style={{ color: '#2E7D32' }} />
                  <div>
                    <p className="text-sm" style={{ color: '#5C635A' }}>Paid Out</p>
                    <p className="text-2xl font-bold" style={{ color: '#2E7D32' }}><Usd value={earnings.total_paid_out} /></p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card style={{ borderColor: '#E3E1DB' }}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <AlertCircle className="h-8 w-8" style={{ color: '#D86B47' }} />
                  <div>
                    <p className="text-sm" style={{ color: '#5C635A' }}>Pending Payout</p>
                    <p className="text-2xl font-bold" style={{ color: '#D86B47' }}><Usd value={earnings.pending_payout} /></p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Bank Details Form */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="mb-6" style={{ borderColor: '#E3E1DB' }}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                <Building2 className="h-5 w-5" />
                Bank Account Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              {bankDetails?.has_bank_details && (
                <div className="mb-6 p-4 rounded-lg" style={{ backgroundColor: bankDetails.bank_verified ? '#E8F5E9' : '#FFF8E7' }}>
                  <div className="flex items-center gap-2 mb-2">
                    {bankDetails.bank_verified ? (
                      <CheckCircle className="h-5 w-5" style={{ color: '#2E7D32' }} />
                    ) : (
                      <AlertCircle className="h-5 w-5" style={{ color: '#D86B47' }} />
                    )}
                    <span className="font-medium" style={{ color: '#163329' }}>
                      {bankDetails.bank_verified ? 'Bank Account Verified' : 'Verification Pending'}
                    </span>
                  </div>
                  <p className="text-sm" style={{ color: '#5C635A' }}>
                    <strong>{bankDetails.bank_name}</strong> - {'*'.repeat((bankDetails.account_number_length || 8) - 4)}{bankDetails.account_number_last4}
                  </p>
                  <p className="text-sm" style={{ color: '#5C635A' }}>
                    Account Holder: {bankDetails.account_holder_name}
                  </p>
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="bank_name">Bank Name *</Label>
                    <Input
                      id="bank_name"
                      value={formData.bank_name}
                      onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                      placeholder="e.g., Chase Bank, Bank of America"
                      required
                      data-testid="bank-name-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="account_holder_name">Account Holder Name *</Label>
                    <Input
                      id="account_holder_name"
                      value={formData.account_holder_name}
                      onChange={(e) => setFormData({ ...formData, account_holder_name: e.target.value })}
                      placeholder="Name as it appears on account"
                      required
                      data-testid="account-holder-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="account_number">Account Number *</Label>
                  <Input
                    id="account_number"
                    type="password"
                    value={formData.account_number}
                    onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                    placeholder={bankDetails?.has_bank_details ? "Enter new account number to update" : "Enter your account number"}
                    required={!bankDetails?.has_bank_details}
                    data-testid="account-number-input"
                  />
                  <p className="text-xs" style={{ color: '#5C635A' }}>
                    Your account number is stored securely and only visible to platform administrators for payouts.
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="routing_number">Routing Number</Label>
                    <Input
                      id="routing_number"
                      value={formData.routing_number}
                      onChange={(e) => setFormData({ ...formData, routing_number: e.target.value })}
                      placeholder="9-digit routing number"
                      data-testid="routing-number-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="swift_code">SWIFT/BIC Code (International)</Label>
                    <Input
                      id="swift_code"
                      value={formData.swift_code}
                      onChange={(e) => setFormData({ ...formData, swift_code: e.target.value })}
                      placeholder="For international transfers"
                      data-testid="swift-code-input"
                    />
                  </div>
                </div>
                
                <Button
                  type="submit"
                  disabled={isSaving}
                  className="w-full sm:w-auto rounded-full"
                  style={{ backgroundColor: '#163329', color: '#FAF9F6' }}
                  data-testid="save-bank-details-button"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    bankDetails?.has_bank_details ? 'Update Bank Details' : 'Save Bank Details'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </motion.div>

        {/* Payment Info Card */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <Card style={{ borderColor: '#E3E1DB' }}>
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                How Payments Work
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 text-sm" style={{ color: '#5C635A' }}>
                <div className="p-4 rounded-lg" style={{ backgroundColor: '#F0EFEA' }}>
                  <h4 className="font-medium mb-2" style={{ color: '#163329' }}>Payment Process:</h4>
                  <ul className="space-y-2">
                    <li>1. Students purchase your courses through the platform</li>
                    <li>2. A platform commission is automatically calculated and deducted</li>
                    <li>3. Your net earnings are tracked in your account</li>
                    <li>4. The Admin will process payouts within 7 working days</li>
                  </ul>
                </div>
                
                <div className="p-4 rounded-lg" style={{ backgroundColor: '#F0EFEA' }}>
                  <h4 className="font-medium mb-2" style={{ color: '#163329' }}>Commission:</h4>
                  <p>
                    The Admin will also deduct the Kit management cost where applicable, and a small platform fee is deducted from each sale to support platform operations.
                    You can view your earnings breakdown in the summary above.
                  </p>
                </div>
                
                <div className="p-4 rounded-lg" style={{ backgroundColor: '#F0EFEA' }}>
                  <h4 className="font-medium mb-2" style={{ color: '#163329' }}>Security:</h4>
                  <p>
                    Your bank details are stored securely and only accessible to platform administrators 
                    for processing payouts. We never share your information with third parties.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default TeacherPaymentSettings;
